export function normalizeSelectedCouponCodes(codes) {
  if (!Array.isArray(codes)) return [];
  const seen = new Set();
  const normalized = [];
  for (const entry of codes) {
    const code = String(entry || "").trim();
    if (!code) continue;
    const key = code.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    normalized.push(code);
  }
  return normalized;
}

/**
 * Resolve cart-drawer coupon suggestion config from section settings.
 * Suggestions on defaults to all public store codes.
 * Only use an allowlist when "show all" is explicitly off and codes are selected.
 */
export function getCartCouponOfferConfig(setting = {}) {
  if (!setting?.showCouponSuggestions) {
    return {
      enabled: false,
      showAll: false,
      selectedCouponCodes: [],
    };
  }

  const selectedCouponCodes = normalizeSelectedCouponCodes(
    setting.selectedCouponCodes
  );
  const useAllowlistOnly =
    setting.showAllCouponCodes === false && selectedCouponCodes.length > 0;

  return {
    enabled: true,
    showAll: !useAllowlistOnly,
    selectedCouponCodes,
  };
}

/** Preview / editor fallback when public discount-codes API is unavailable. */
export const EDITOR_FALLBACK_CART_COUPONS = [
  {
    code: "HELLO10",
    title: "Flat 10% off on all orders",
    subline: "HELLO10",
    summary: "Flat 10% off on all orders. No minimum value.",
    description: "Flat 10% off on all orders. No minimum value.",
  },
  {
    code: "FIRSTORDER",
    title: "10% off entire order",
    subline: "FIRSTORDER",
    summary: "10% off entire order • One use per customer",
    description: "10% off entire order • One use per customer",
  },
  {
    code: "SAVE15",
    title: "Flat 15% off on orders",
    subline: "SAVE15",
    summary: "Flat 15% off on prepaid orders",
    description: "Flat 15% off on prepaid orders",
  },
];

export function isLikelyShopifyShopDomain(shop = "") {
  const value = String(shop || "").trim().toLowerCase();
  if (!value) return false;
  if (
    value === "localhost" ||
    value === "127.0.0.1" ||
    value.endsWith(".local") ||
    value.includes("trycloudflare.com")
  ) {
    return false;
  }
  return value.includes(".") || value.endsWith(".myshopify.com");
}

export function shouldFetchCartCouponSuggestions(setting = {}) {
  const config = getCartCouponOfferConfig(setting);
  if (!config.enabled) return false;
  return config.showAll || config.selectedCouponCodes.length > 0;
}

export function filterCartDrawerCoupons(coupons = [], setting = {}) {
  const config = getCartCouponOfferConfig(setting);
  if (!config.enabled) return [];
  if (config.showAll) {
    return Array.isArray(coupons) ? coupons.filter((c) => c?.code) : [];
  }
  if (!config.selectedCouponCodes.length) return [];

  const allowed = new Set(
    config.selectedCouponCodes.map((code) => code.toLowerCase())
  );

  return (Array.isArray(coupons) ? coupons : []).filter((coupon) =>
    allowed.has(String(coupon?.code || "").trim().toLowerCase())
  );
}

/** Best-effort savings estimate from Shopify summary text + cart subtotal (cents). */
export function estimateCartCouponSavingsCents(cart, coupon = {}) {
  const base =
    Number(cart?.items_subtotal_price) ||
    Number(cart?.original_total_price) ||
    Number(cart?.total_price) ||
    0;
  if (base <= 0) return 0;

  const haystack = [
    coupon?.title,
    coupon?.summary,
    coupon?.subline,
    coupon?.description,
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");

  const pctMatch = haystack.match(/(\d+(?:\.\d+)?)\s*%/);
  if (pctMatch) {
    const pct = Number(pctMatch[1]);
    if (Number.isFinite(pct) && pct > 0) {
      return Math.max(0, Math.round((base * pct) / 100));
    }
  }

  const flatMatch = haystack.match(
    /(?:₹|rs\.?|usd|\$|eur|€)\s*([\d,]+(?:\.\d+)?)/i
  );
  if (flatMatch) {
    const major = Number(String(flatMatch[1]).replace(/,/g, ""));
    if (Number.isFinite(major) && major > 0) {
      // Shopify Ajax cart money is in cents/subunits.
      return Math.max(0, Math.round(major * 100));
    }
  }

  return 0;
}

export function pickBestValueCouponCode(coupons = [], cart = null) {
  if (!Array.isArray(coupons) || coupons.length === 0) return "";
  let bestCode = String(coupons[0]?.code || "").trim();
  let bestCents = -1;
  for (const coupon of coupons) {
    const code = String(coupon?.code || "").trim();
    if (!code) continue;
    const cents = estimateCartCouponSavingsCents(cart, coupon);
    if (cents > bestCents) {
      bestCents = cents;
      bestCode = code;
    }
  }
  return bestCode;
}

export function resolveCartCouponDisplay(coupon = {}) {
  const code = String(coupon?.code || "").trim();
  const headline = String(coupon?.title || "").trim();
  const codeLine = String(coupon?.subline || coupon?.summary || "").trim();

  const displayHeadline = headline || code;
  const displayCode =
    codeLine && codeLine.toLowerCase() !== displayHeadline.toLowerCase()
      ? codeLine
      : code && code.toLowerCase() !== displayHeadline.toLowerCase()
        ? code
        : "";

  return {
    code,
    headline: displayHeadline,
    codeLabel: displayCode,
  };
}

export function syncSelectedCouponDetails(selectedCodes = [], couponOptions = []) {
  const codes = normalizeSelectedCouponCodes(selectedCodes);
  const optionByCode = new Map(
    (couponOptions || []).map((option) => [
      String(option.code || "").toLowerCase(),
      option,
    ])
  );

  return codes.map((code) => {
    const option = optionByCode.get(code.toLowerCase());
    if (!option) {
      return { code, title: code, subline: "" };
    }
    return {
      code: option.code,
      title: option.title,
      subline: option.subline || "",
    };
  });
}

export function getEditorPreviewCartCoupons(setting = {}) {
  const config = getCartCouponOfferConfig(setting);
  if (!config.enabled) return [];

  const details = Array.isArray(setting?.selectedCouponDetails)
    ? setting.selectedCouponDetails
    : [];

  if (config.showAll) {
    if (details.length) {
      return details
        .map((entry) => ({
          code: String(entry?.code || "").trim(),
          title: String(entry?.title || entry?.code || "").trim(),
          subline: String(entry?.subline || "").trim(),
          summary: String(entry?.subline || entry?.summary || "").trim(),
        }))
        .filter((entry) => entry.code);
    }
    return config.selectedCouponCodes.map((code) => ({
      code,
      title: code,
      subline: "",
      summary: "",
    }));
  }

  if (!config.selectedCouponCodes.length) return [];

  const allowed = new Set(
    config.selectedCouponCodes.map((code) => code.toLowerCase())
  );

  const fromDetails = details
    .filter((entry) =>
      allowed.has(String(entry?.code || "").trim().toLowerCase())
    )
    .map((entry) => ({
      code: String(entry.code || "").trim(),
      title: String(entry.title || entry.code || "").trim(),
      subline: String(entry.subline || "").trim(),
      summary: String(entry.subline || "").trim(),
    }));

  if (fromDetails.length) return fromDetails;

  return config.selectedCouponCodes.map((code) => ({
    code,
    title: code,
    subline: "",
    summary: "",
  }));
}

export function formatCartCouponOption(rule = {}, code = "") {
  const normalizedCode = String(code || "").trim();
  const summary = String(rule?.summary || "").trim();
  const ruleTitle = String(rule?.title || "").trim();
  const codeMatchesTitle =
    ruleTitle && ruleTitle.toLowerCase() === normalizedCode.toLowerCase();

  const headline = summary || (codeMatchesTitle ? normalizedCode : ruleTitle || normalizedCode);
  const subline =
    summary || !codeMatchesTitle
      ? normalizedCode
      : "";

  return {
    code: normalizedCode,
    title: headline,
    summary: summary || (codeMatchesTitle ? "" : ruleTitle),
    subline:
      subline && subline.toLowerCase() !== headline.toLowerCase() ? subline : "",
  };
}

export function getStoreCouponOptionsFromPriceRules(priceRules = []) {
  const options = [];
  const seen = new Set();

  for (const rule of priceRules || []) {
    const discountCodes = Array.isArray(rule?.discountCodes)
      ? rule.discountCodes
      : [];

    if (discountCodes.length) {
      for (const entry of discountCodes) {
        const code = String(entry?.code || "").trim();
        if (!code) continue;
        const key = code.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        options.push(formatCartCouponOption(rule, code));
      }
      continue;
    }

    const fallbackCode = String(rule?.title || "").trim();
    if (!fallbackCode) continue;
    const key = fallbackCode.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    options.push(formatCartCouponOption(rule, fallbackCode));
  }

  return options;
}

export function normalizeCartCouponRecord(coupon = {}) {
  const code = String(coupon?.code || "").trim();
  if (!code) return null;

  const subline = String(coupon?.subline || "").trim();
  const title = String(coupon?.title || "").trim();
  const summary = String(coupon?.summary || "").trim();
  const description = String(coupon?.description || summary || "").trim();

  if (subline) {
    return {
      code,
      title: title || code,
      subline,
      summary: subline,
      description: description || title || "",
    };
  }

  if (
    title &&
    summary &&
    title.toLowerCase() !== code.toLowerCase() &&
    summary.toLowerCase() === code.toLowerCase()
  ) {
    return { code, title, subline: summary, summary, description: title };
  }

  if (
    title.toLowerCase() === code.toLowerCase() &&
    summary &&
    summary.toLowerCase() !== code.toLowerCase()
  ) {
    return {
      code,
      title: summary,
      subline: code,
      summary: code,
      description: summary,
    };
  }

  const formatted = formatCartCouponOption(
    { title: coupon?.title, summary: coupon?.summary },
    code
  );

  return {
    code: formatted.code,
    title: formatted.title,
    subline: formatted.subline || "",
    summary: formatted.subline || "",
    description: description || formatted.title || "",
  };
}

export function resolveVisibleCartCoupons(setting = {}, apiCoupons = []) {
  if (!shouldFetchCartCouponSuggestions(setting)) return [];

  const config = getCartCouponOfferConfig(setting);
  const apiNormalized = (apiCoupons || [])
    .map(normalizeCartCouponRecord)
    .filter(Boolean);
  const apiByCode = new Map(
    apiNormalized.map((coupon) => [coupon.code.toLowerCase(), coupon])
  );

  if (config.showAll) {
    if (apiNormalized.length) return filterCartDrawerCoupons(apiNormalized, setting);
    const preview = getEditorPreviewCartCoupons(setting);
    if (preview.length) return preview;
    return EDITOR_FALLBACK_CART_COUPONS.map(normalizeCartCouponRecord).filter(
      Boolean
    );
  }

  let coupons = getEditorPreviewCartCoupons(setting).map((coupon) => {
    const needsEnrich =
      !coupon.subline &&
      (!coupon.title || coupon.title.toLowerCase() === coupon.code.toLowerCase());
    if (!needsEnrich) return coupon;
    return apiByCode.get(coupon.code.toLowerCase()) || coupon;
  });

  const hasReadableLabels = coupons.some(
    (coupon) =>
      coupon.subline ||
      (coupon.title && coupon.title.toLowerCase() !== coupon.code.toLowerCase())
  );

  if (!hasReadableLabels && apiByCode.size) {
    coupons = filterCartDrawerCoupons([...apiByCode.values()], setting);
  }

  return coupons;
}

export function formatPublicCartCoupon(coupon = {}) {
  return normalizeCartCouponRecord(coupon) || {
    code: "",
    title: "",
    subline: "",
    summary: "",
  };
}

export function getShopifyAdminDiscountsUrl(shop) {
  const rawShop = String(shop || "").trim();
  const handle = rawShop.replace(/\.myshopify\.com$/i, "").trim();
  if (!handle) {
    return "https://admin.shopify.com/discounts";
  }
  return `https://admin.shopify.com/store/${handle}/discounts`;
}
